import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Accordion, AccordionDetails, AccordionSummary, Chip, Stack, Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

import { SHADOWS } from '../../../theme/muiTheme';

export default function ScopeSection({
  title,
  icon: Icon,
  tone,
  attentionCount = 0,
  headerActions,
  children,
  fillContent = false,
  sx,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const needsAttention = attentionCount > 0;
  const accentColor = theme.palette.mode === 'dark' ? tone.color : tone.dark;

  return (
    <Accordion
      expanded
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: alpha(theme.palette.text.primary, 0.13),
        borderRadius: '12px !important',
        boxShadow: theme.palette.mode === 'dark' ? 'none' : SHADOWS.e1,
        overflow: 'hidden',
        ...(fillContent && {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          '& .MuiCollapse-root': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
          },
          '& .MuiCollapse-wrapper': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          },
          '& .MuiCollapse-wrapperInner': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          },
          '& .MuiAccordion-region': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
          },
        }),
        '&::before': { display: 'none' },
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? alpha(tone.color, 0.18) : tone.bg,
          borderLeft: 3,
          borderLeftColor: accentColor,
          minHeight: 52,
          px: { xs: 1.5, sm: 2.25 },
          '&.Mui-expanded': { minHeight: 52 },
          '& .MuiAccordionSummary-content': { my: 1.25, minWidth: 0 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.25 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ minWidth: 0, width: '100%', pr: 1, flexWrap: 'wrap' }}
        >
          <Stack direction="row" alignItems="center" spacing={1.1} sx={{ minWidth: 0 }}>
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                bgcolor: alpha(tone.color, theme.palette.mode === 'dark' ? 0.13 : 0.07),
                borderRadius: 1.25,
                color: accentColor,
                flexShrink: 0,
                height: 30,
                width: 30,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Stack>
            <Typography
              component="h2"
              variant="subtitle2"
              sx={{ color: 'text.primary', fontWeight: 800, textTransform: 'none' }}
            >
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {headerActions}
            <Chip
              icon={needsAttention ? <ErrorOutlineRoundedIcon /> : <CheckCircleRoundedIcon />}
              label={needsAttention
                ? t('dataInput.status.attention', { count: attentionCount })
                : t('dataInput.status.complete')}
              size="small"
              sx={{
                bgcolor: alpha(
                  needsAttention ? theme.palette.warning.main : theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.1 : 0.045,
                ),
                border: 1,
                borderColor: alpha(
                  needsAttention ? theme.palette.warning.main : theme.palette.success.main,
                  0.18,
                ),
                color: needsAttention ? 'warning.main' : 'success.main',
                flexShrink: 0,
                '& .MuiChip-icon': { color: 'inherit', fontSize: 15 },
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.035 : 0.018),
          borderTop: 1,
          borderColor: 'divider',
          p: { xs: 1.5, sm: 2 },
          ...(fillContent && {
            display: 'flex',
            flex: 1,
            minHeight: 0,
            '& > .MuiStack-root': {
              display: 'flex',
              flex: 1,
              minHeight: 0,
              '& > *': { flex: 1, minHeight: 0 },
            },
          }),
        }}
      >
        <Stack spacing={1.5}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}
